/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

// eslint-disable-next-line import/no-cycle
import {
  CARD_LIST_BREAKPOINTS_CONFIG,
  DESKTOP_LIST_IMAGE_CONFIG,
  MOBILE_CARD_IMAGE_CONFIG,
  ShowMoreGridSection,
} from '@coursera-web/cds-labs';
import type { GridConfig, ShowMoreGridSectionProps } from '@coursera-web/cds-labs';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type {
  CarouselFilter as CarouselFilterTrackingData,
  PageConfigComponent as PageConfigComponentTrackingData,
  Recommender,
} from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import { COLLECTION_CAROUSEL_PRODUCT_CARD_IMAGE_HEIGHT } from 'bundles/collections-carousel/constants';
import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import { getCollectionType, getDataE2ETag } from 'bundles/collections-common/utils/collectionsUtils';
import useIsLohp from 'bundles/collections-common/utils/useIsLohp';
import withSingleTracked, { TrackedCdsButton } from 'bundles/common/components/withSingleTracked';
import { LearningPathProductCardImage } from 'bundles/page-config-common/components/enterprise/EnterpriseLearningPathProductCard';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { PathwayContentBadge } from 'bundles/product-card/components/Badges';
import LearningProductCard from 'bundles/product-card/components/LearningProductCard';
import LearningProductCardBadge from 'bundles/product-card/components/LearningProductCardBadge';
import LearningProductCardImage from 'bundles/product-card/components/LearningProductCardImage';
import {
  getImagePropOverrides,
  getPartnerNamesAndLogos,
} from 'bundles/product-card/components/utils/productCardV2Utils';
import { getProductCardStatusTags } from 'bundles/product-card/utils/getProductCardStatusTags';
import { PRODUCT_CARD_LAZY_LOAD_THRESHOLD } from 'bundles/product-card/utils/productCardUtils';
import { LEARNING_PRODUCTS, getTranslatedDifficulty } from 'bundles/search-common/constants';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';

import _t from 'i18n!nls/collections-common';

const TrackedLinkButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps<'a'>>(
  // @ts-expect-error
  Button
);

export const styles = {
  /* TrackedLink2 adds an extra div that interferes with the card styling.
  This wrapper div applies style to that extra div to ensure the cards are styled correctly. */
  targetCardWrapper: css`
    height: 100%;

    > * {
      height: inherit;
    }

    .cds-ProductCard-list {
      /* temporaryly force list card to have border */
      border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }
  `,
  desktopListCard: css`
    .cds-ProductCard-listPreviewImage {
      width: ${DESKTOP_LIST_IMAGE_CONFIG.width}px;
      height: ${DESKTOP_LIST_IMAGE_CONFIG.height}px;
      max-height: ${DESKTOP_LIST_IMAGE_CONFIG.height}px !important; /* override CDS */
    }
  `,
  titleLink: css`
    color: var(--cds-color-neutral-primary);
  `,
  secondaryCta: css`
    padding-top: var(--cds-spacing-50);
  `,
  removePadding: css`
    padding: 0;
  `,
  sessionContent: css`
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
  `,
};

type Props = {
  collection: CarouselCollection;
  collectionIndex?: number;
  itemsGridConfig?: GridConfig;
  showAllEntries?: boolean;
  renderTitle?: () => React.ReactNode;
  productCardTrackingName?: string | null;
  eventingData?: {
    searchQuery?: string;
    page?: string;
    filtersApplied?: Record<string, string[]>;
    searchIndex?: string;
    searchIndexPosition?: number;
    pageConfigComponent?: PageConfigComponentTrackingData;
    carouselFilter?: CarouselFilterTrackingData;
  };
  collectionRecommender?: Recommender;
  customComponentAtIndex?: { index: number; component: React.JSX.Element };
  displayRows?: number;
  removeExtraPadding?: boolean;
  disableDesktopListView?: boolean;
  cardSpacing?: Record<string, unknown>;
};

type CollectionTitleProps = { title: string; url?: string | null };
const CollectionTitle = ({ title, url }: CollectionTitleProps) => {
  const trackingName = 'collection_carousel_title_link_to_collection_page';

  const TrackedTitle = (
    <TrackedLink2 href={url} data={{ url }} trackingName={trackingName} css={styles.titleLink}>
      {title}
    </TrackedLink2>
  );
  const collectionTitle = url ? TrackedTitle : title;
  return (
    <Typography2 variant="titleSmall" component="h2">
      {collectionTitle}
    </Typography2>
  );
};

const ProductCardCollection = ({
  collection,
  itemsGridConfig,
  showAllEntries = false,
  collectionIndex,
  renderTitle,
  productCardTrackingName,
  eventingData,
  collectionRecommender,
  customComponentAtIndex,
  displayRows,
  removeExtraPadding,
  disableDesktopListView,
  cardSpacing,
}: Props) => {
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const track = useTracker();
  const isLohp = useIsLohp();
  const { entities = [] } = collection;
  const shouldShowDesktopListView = entities.length < 3 && !isMobile && !isTabletOrSmaller && !disableDesktopListView;
  const shouldShowListView = shouldShowDesktopListView || isTabletOrSmaller || isMobile;

  let expandButtonVariant: 'primary' | 'secondary' | 'ghost' = 'secondary';
  if (isLohp) expandButtonVariant = 'primary';

  const {
    title,
    collectionLink,
    header,
    footer,
    collectionTrackingData,
    collectionId,
    expandButtonUrl,
    expandedExternalLinkButtonText,
    expandedExternalLinkButtonUrl,
    renderCustomPrimaryCta,
  } = collection;

  const collectionCtaTrackingName = collectionTrackingData?.collectionName ?? title;
  const trackingId: string | undefined = collectionTrackingData?.trackingId
    ? `${collectionTrackingData?.trackingId}`
    : '';
  const trackingData = {
    title,
    collectionId,
    ...collectionTrackingData,
  };

  let trackingE2eId = '';
  if (collectionTrackingData && collectionTrackingData.collectionId) {
    trackingE2eId = collectionTrackingData.collectionId as string;
  }

  const getProductCardImageProps = (imagePropsOverride?: {
    desktop?: { height: number; width: number };
    mobile?: { height: number; width: number };
  }) => {
    if (shouldShowDesktopListView) {
      return imagePropsOverride?.desktop ?? DESKTOP_LIST_IMAGE_CONFIG;
    } else if (shouldShowListView) {
      return imagePropsOverride?.mobile ?? MOBILE_CARD_IMAGE_CONFIG;
    }
    return { height: COLLECTION_CAROUSEL_PRODUCT_CARD_IMAGE_HEIGHT };
  };

  let numDisplayedRows = isMobile ? 2 : 1;

  if (showAllEntries) {
    numDisplayedRows = entities.length;
  }
  const trackingName = productCardTrackingName ?? 'collection_product_card';
  const isEqp = useIsEqp();
  const isConsumerSearch = useIsConsumerSerp();

  const expandButtonSize = isLohp && !isMobile ? 'medium' : 'small';

  const renderExpandButton: ShowMoreGridSectionProps<CollectionEntity>['renderExpandButton'] = ({
    onClick,
    defaultLabel,
    isExpanded,
    showMoreCount,
    ...rest
  }) => {
    return (
      <TrackedCdsButton
        aria-label={`${defaultLabel}, ${title}`}
        trackingName="show_more"
        trackingData={{
          ...trackingData,
          title,
          isExpanded,
          showMoreCount,
        }}
        size={expandButtonSize}
        variant={expandButtonVariant}
        onClick={() => {
          const isCollapseButton = isExpanded && showMoreCount === 0;
          track(isCollapseButton ? 'collapse_carousel' : 'expand_carousel', {
            carousel: {
              id: trackingData.collectionId,
              name: collection?.collectionTrackingData?.collectionName?.toString() ?? '',
              recommender: (collectionTrackingData?.carouselRecommender as Recommender) ?? collection.recommender,
            },
          });
          onClick();
        }}
        fullWidth={isMobile}
        {...rest}
      >
        {defaultLabel}
      </TrackedCdsButton>
    );
  };

  const placeholderItem = { id: 'PLACEHOLDER_ITEM_ID' } as CollectionEntity;

  const entitiesToShow = customComponentAtIndex
    ? [
        ...entities.slice(0, customComponentAtIndex.index),
        placeholderItem,
        ...entities.slice(customComponentAtIndex.index),
      ]
    : entities;

  return (
    <ShowMoreGridSection<CollectionEntity>
      css={removeExtraPadding && styles.removePadding}
      className="rc-ProductCardCollection"
      title={title}
      renderTitle={renderTitle ?? (() => header || <CollectionTitle title={title} url={collectionLink} />)}
      renderFooter={() => footer}
      items={entitiesToShow}
      itemsGridConfig={shouldShowListView ? CARD_LIST_BREAKPOINTS_CONFIG : itemsGridConfig}
      defaultRows={displayRows || numDisplayedRows}
      spacing={cardSpacing ?? { xs: 8, sm: 24 }}
      renderItem={({ item, itemIndex, itemRef }) => {
        if (customComponentAtIndex && itemIndex === customComponentAtIndex.index) {
          return customComponentAtIndex.component;
        }
        const translatedDifficulty = getTranslatedDifficulty(item?.productDifficultyLevel ?? '');
        const translatedProductDifficultyLevel = !translatedDifficulty
          ? item?.productDifficultyLevel
          : translatedDifficulty;
        return (
          <div css={[styles.targetCardWrapper, shouldShowDesktopListView && styles.desktopListCard]}>
            <LearningProductCard
              ImageComponent={
                (item?.productType as string) === LEARNING_PRODUCTS.LearningPath ? (
                  <LearningPathProductCardImage src={item.imageUrl ?? ''} />
                ) : (
                  <LearningProductCardImage
                    imageUrl={item.imageUrl}
                    enableLazyLoading={itemIndex > PRODUCT_CARD_LAZY_LOAD_THRESHOLD}
                    enableFluidWidthForLazyImg
                    imageParams={{
                      ...getProductCardImageProps(),
                      ...getImagePropOverrides(item.partners ?? [], item.imageUrl ?? ''),
                    }}
                  />
                )
              }
              name={item.name || ''}
              titleComponent={item?.titleComponent || 'h3'}
              linkProps={{
                href: item?.link,
                target: item.forceOpenInNewTab ? '_blank' : undefined,
                component: TrackedLink2,
              }}
              partners={
                item?.partners
                  ? getPartnerNamesAndLogos(item.partners, !!item?.coBrandingEnabled, item.partnerLogos)
                  : []
              }
              skills={item?.skills}
              bodyContent={
                item?.session ? (
                  <Typography2 component="p" variant="bodySecondary" css={styles.sessionContent}>
                    <b>{item.session}</b>
                  </Typography2>
                ) : null
              }
              numProductRatings={item?.numProductRatings}
              avgProductRating={item?.avgProductRating}
              productType={item?.productType}
              productDifficultyLevel={translatedProductDifficultyLevel}
              productDuration={item?.productDuration}
              statusTags={getProductCardStatusTags({ isNew: false, statusTags: item?.statusTags, slug: item?.slug })}
              badge={
                <>
                  <LearningProductCardBadge
                    customBadge={item?.isPathwayContent ? <PathwayContentBadge /> : undefined}
                    productType={item?.productType}
                  />
                  {item?.badges}
                </>
              }
              variant={shouldShowListView ? 'list' : 'grid'}
              id={item.id}
              slug={item?.slug}
              cardIndex={itemIndex}
              trackingProps={{
                trackedLinkProps: {
                  trackingName,
                  withVisibilityTracking: true,
                  requireFullyVisible: true,
                  data: {
                    itemIndex,
                    collectionId,
                    ...collection.collectionTrackingData,
                    ...(item.trackingData || { id: item.id, item }),
                    collectionIndex:
                      collectionIndex !== undefined
                        ? collectionIndex
                        : collection?.collectionTrackingData?.collectionIndex,
                    ...((isConsumerSearch || isEqp) && {
                      objectID: item.id || undefined,
                      hitPosition: itemIndex,
                      queryID: eventingData?.searchQuery || '',
                      query: eventingData?.searchQuery || '',
                      listIndex: Number(eventingData?.page) || 1,
                      noResults: false,
                      filtersApplied: eventingData?.filtersApplied,
                      indexName: eventingData?.searchIndex,
                      indexPosition: eventingData?.searchIndexPosition,
                    }),
                  },
                  refAlt: itemRef,
                },
                additionalProductCardTrackingData: {
                  pageConfigComponent: eventingData?.pageConfigComponent,
                },
                additionalV3TrackingData: {
                  carousel: {
                    id: (collectionId as string) ?? '',
                    name: collection?.collectionTrackingData?.collectionName ?? '',
                    section:
                      collectionIndex !== undefined
                        ? collectionIndex
                        : collection?.collectionTrackingData?.collectionIndex,
                    model: collectionId as string,
                    type: getCollectionType(collectionId as string),
                    recommender: collectionTrackingData?.carouselRecommender as Recommender | undefined,
                    filter: eventingData?.carouselFilter,
                  },
                },
              }}
              recommender={collectionRecommender}
            />
          </div>
        );
      }}
      renderExpandButton={(expandButtonProps) => (
        <React.Fragment>
          {renderCustomPrimaryCta?.({
            size: expandButtonSize,
            fullWidth: isMobile,
          })}
          {renderExpandButton(expandButtonProps)}
        </React.Fragment>
      )}
      renderSecondaryCta={() => {
        if (!expandButtonUrl && !expandedExternalLinkButtonUrl) return null;
        return (
          <div className="pcc-secondary-cta" css={isMobile && isLohp ? styles.secondaryCta : null}>
            <TrackedLinkButton
              trackingName="secondary_CTA"
              trackingData={trackingData}
              size={isLohp && !isMobile ? 'medium' : 'small'}
              variant={isLohp || isMobile ? 'secondary' : 'ghost'}
              component="a"
              href={expandButtonUrl || expandedExternalLinkButtonUrl || undefined}
              icon={!isMobile && !isTabletOrSmaller ? <ArrowNextIcon /> : undefined}
              fullWidth={isMobile}
              onClick={() => {
                track('click_button', {
                  button: {
                    name: 'view_all_products',
                    linkName: !isLohp
                      ? `${expandedExternalLinkButtonText ?? ''} - ${collectionCtaTrackingName}`
                      : `View all - ${collectionCtaTrackingName}`,
                    linkURL: expandButtonUrl || expandedExternalLinkButtonUrl || undefined,
                  },
                });
              }}
            >
              {!isLohp ? expandedExternalLinkButtonText : _t('View all')}
            </TrackedLinkButton>
          </div>
        );
      }}
      data-e2e={getDataE2ETag(trackingE2eId || trackingId)}
      aria-label={_t('#{title} Collection', { title: !header ? title : '' })}
    />
  );
};
export default ProductCardCollection;
